import React, { useState } from 'react'
import { Check, LogOut, Pencil, RefreshCw, Trash2, X, Wifi, WifiOff } from 'lucide-react'
import { t } from '@okclawpro/i18n'
import type { RemoteUser } from './remote-auth'
import { deleteDevice, renameDevice, type RemoteDeviceView } from './remote-devices'

export interface DeviceListPageProps {
  user: RemoteUser | null
  devices: RemoteDeviceView[]
  isLoading: boolean
  error?: string
  onRefresh: () => void
  onLogout: () => void
  onConnect: (device: RemoteDeviceView) => void
  onDeviceRenamed: (device: RemoteDeviceView) => void
  onDeviceDeleted: (deviceId: string) => void
}

function formatLastSeenAt(value?: string | null): string {
  if (!value) return t('webui.remote.device.lastSeenNever')
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return t('webui.remote.device.lastSeenUnknown')
  return date.toLocaleString()
}

export default function DeviceListPage({
  user,
  devices,
  isLoading,
  error,
  onRefresh,
  onLogout,
  onConnect,
  onDeviceRenamed,
  onDeviceDeleted,
}: DeviceListPageProps) {
  const [editingDeviceId, setEditingDeviceId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const [renameError, setRenameError] = useState('')
  const [renamingDeviceId, setRenamingDeviceId] = useState<string | null>(null)
  const [deletingDeviceId, setDeletingDeviceId] = useState<string | null>(null)

  const startEdit = (device: RemoteDeviceView) => {
    setEditingDeviceId(device.deviceId)
    setEditingName(device.name)
    setRenameError('')
  }

  const submitRename = (device: RemoteDeviceView) => {
    const nextName = editingName.trim()
    if (!nextName || renamingDeviceId) return
    setRenamingDeviceId(device.deviceId)
    setRenameError('')
    void renameDevice(device.deviceId, nextName)
      .then((renamedDevice) => {
        onDeviceRenamed(renamedDevice)
        setEditingDeviceId(null)
      })
      .catch(() => setRenameError(t('webui.remote.device.renameFailed')))
      .finally(() => setRenamingDeviceId(null))
  }

  const submitDelete = (device: RemoteDeviceView) => {
    if (deletingDeviceId) return
    const confirmed = window.confirm(t('webui.remote.device.deleteConfirm', { name: device.name }))
    if (!confirmed) return

    setDeletingDeviceId(device.deviceId)
    setRenameError('')
    void deleteDevice(device.deviceId)
      .then(() => {
        onDeviceDeleted(device.deviceId)
      })
      .catch(() => setRenameError(t('webui.remote.device.deleteFailed')))
      .finally(() => setDeletingDeviceId(null))
  }

  return (
    <main className="min-h-screen overflow-auto bg-muted/30 px-4 py-5 font-sans text-foreground sm:px-6">
      <div className="mx-auto flex w-full max-w-[920px] flex-col gap-4">
        <header className="flex flex-col gap-3 rounded-lg border border-border/70 bg-background px-4 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div>
            <p className="text-[12px] font-medium uppercase text-muted-foreground">{t('webui.remote.title')}</p>
            <h1 className="mt-1 text-[22px] font-semibold leading-7">{t('webui.remote.devices.title')}</h1>
            <p className="mt-1 text-[13px] text-muted-foreground">
              {user?.name || user?.email || user?.phone || t('webui.remote.devices.signedIn')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-background text-muted-foreground transition-colors hover:text-foreground"
              onClick={onRefresh}
              title={t('webui.remote.refresh')}
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              type="button"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-background text-muted-foreground transition-colors hover:text-foreground"
              onClick={onLogout}
              title={t('webui.remote.logout')}
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </header>

        {error ? (
          <p className="rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-[13px] text-destructive">{error}</p>
        ) : null}
        {renameError ? (
          <p className="rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-[13px] text-destructive">{renameError}</p>
        ) : null}

        {devices.length === 0 && !isLoading ? (
          <section className="rounded-lg border border-dashed border-border bg-background px-5 py-10 text-center">
            <h2 className="text-[16px] font-semibold">{t('webui.remote.empty.title')}</h2>
            <p className="mx-auto mt-2 max-w-[420px] text-[13px] leading-5 text-muted-foreground">{t('webui.remote.empty.description')}</p>
          </section>
        ) : (
          <section className="grid gap-3">
            {devices.map((device) => {
              const isEditing = editingDeviceId === device.deviceId
              return (
                <article
                  key={device.deviceId}
                  className="rounded-lg border border-border/70 bg-background px-4 py-4 shadow-sm"
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0 flex-1">
                      {isEditing ? (
                        <form
                          className="flex max-w-[420px] items-center gap-2"
                          onSubmit={(event) => {
                            event.preventDefault()
                            submitRename(device)
                          }}
                        >
                          <input
                            className="h-9 min-w-0 flex-1 rounded-md border border-border bg-background px-3 text-[14px] outline-none focus:border-ring focus:ring-2 focus:ring-ring/15"
                            value={editingName}
                            onChange={(event) => setEditingName(event.target.value)}
                            autoFocus
                          />
                          <button type="submit" className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-zinc-950 text-white disabled:bg-zinc-300" disabled={renamingDeviceId === device.deviceId || !editingName.trim()}>
                            <Check className="h-4 w-4" />
                          </button>
                          <button type="button" className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border text-muted-foreground hover:text-foreground" onClick={() => setEditingDeviceId(null)}>
                            <X className="h-4 w-4" />
                          </button>
                        </form>
                      ) : (
                        <div className="flex min-w-0 items-center gap-2">
                          <h2 className="truncate text-[16px] font-semibold">{device.name}</h2>
                          <button
                            type="button"
                            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            onClick={() => startEdit(device)}
                            title={t('webui.remote.rename')}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-muted-foreground">
                        <span className={`inline-flex items-center gap-1 ${device.online ? 'text-emerald-600 dark:text-emerald-400' : ''}`}>
                          {device.online ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
                          {device.online ? t('webui.remote.online') : t('webui.remote.offline')}
                        </span>
                        <span>{t('webui.remote.connectionCount', { count: String(device.connectionCount) })}</span>
                        <span>{t('webui.remote.lastSeenAt', { time: formatLastSeenAt(device.lastSeenAt) })}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={!device.online}
                      className="inline-flex h-10 items-center justify-center rounded-md bg-zinc-950 px-4 text-[13px] font-semibold text-white transition-colors hover:bg-zinc-800 disabled:cursor-not-allowed disabled:bg-zinc-300 disabled:text-zinc-500 dark:bg-zinc-50 dark:text-zinc-950 dark:hover:bg-zinc-200 dark:disabled:bg-zinc-700 dark:disabled:text-zinc-400"
                      onClick={() => onConnect(device)}
                    >
                      {device.online ? t('webui.remote.connect') : t('webui.remote.disconnectedByDesktop')}
                    </button>
                    <button
                      type="button"
                      className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive disabled:cursor-not-allowed disabled:opacity-60"
                      onClick={() => submitDelete(device)}
                      disabled={deletingDeviceId === device.deviceId}
                      title={t('webui.remote.device.delete')}
                    >
                      <Trash2 className={`h-4 w-4 ${deletingDeviceId === device.deviceId ? 'animate-pulse' : ''}`} />
                    </button>
                  </div>
                </article>
              )
            })}
          </section>
        )}
      </div>
    </main>
  )
}
